import { invoke } from '@tauri-apps/api/core';
import { huddleWindowFeatures, huddleWindowName } from '@avslack/shared';
import { isDesktop } from './runtime';
import { closeServiceView, handleServiceCall } from './service-call';

/**
 * A huddle KULON ablakban fut.
 *
 * Ket oka van, es mindketto gyakorlati:
 *
 *  - **Nagyobb felulet.** A resztvevok nagy profilkeppel es kameraval, a
 *    megosztott kepernyo olvashato mereten - ez egy oldalsavba tuzott
 *    panelbe nem fer bele.
 *
 *  - **Tulel mindent.** A fo ablak bezarasa, ujratoltese vagy egy elgepelt
 *    navigacio nem szakitja meg a hivast, mert a media-kapcsolatot a kulon
 *    ablak tartja. Ez pontosan az a hibaosztaly, ami miatt a projekt elindult.
 *
 * A "egy felhasznalo egyszerre egy helyen" szabalyt a meglevo
 * BroadcastChannel-egyeztetes tartja be: az uj ablak "atveszi" a hivast, es a
 * fo ablak (ha eppen benne lenne) csendben lezarja magat. A SZUNETELTETETT
 * hivasok kivetelek - azok nem szolnak es nem hallatszanak, ezert nyugodtan
 * futhat beloluk tobb is a masik ablak mellett.
 */
/**
 * A `hivas` kapcsolo: az ablak BEJOVO HIVASKENT nyilik.
 *
 * Ilyenkor nem lep be magatol, hanem csorog es megvarja a dontest. Ugyanaz az
 * ablak, mas belepesi pont - igy a hivott ugyanazt a feluletet latja, amiben a
 * beszelgetes zajlani fog, nem kell atvaltania sehova.
 */
export const INCOMING_PARAM = 'hivas';

/**
 * TELEFONON a hivas az app SAJAT ablakaban fut, nem kulon ablakban (K2).
 *
 * Mobilon a `window.open` nem "masik ablak": a telepitett PWA-bol kidob a
 * bongeszobe, egy uj fulre, bejelentkezes es engedelyek nelkul - vagy a
 * bongeszo egyszeruen blokkolja. A natív appok is teljes kepernyon hivnak.
 * Ilyenkor a hivas egy utvonal az appon belul (`/huddle/...`), es a vege
 * utan visszalepunk oda, ahonnan jottunk.
 */
export function huddleInline(): boolean {
  if (isDesktop() || typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/** Navigacio az appon belul - a router kesleltetett betoltesevel (korkoros import). */
async function navigateInApp(href: string): Promise<void> {
  const { router } = await import('../router');
  await router.navigate({ href });
}

export async function openHuddleWindow(
  channelId: string,
  options: { incoming?: boolean; focusExisting?: boolean } = {},
): Promise<boolean> {
  const path = options.incoming
    ? `/huddle/${channelId}?${INCOMING_PARAM}=1`
    : `/huddle/${channelId}`;

  /*
    VEVOSZOLGALATI MOD (docs/20): nyitott lebego panelnel a hivas a fo ablakban fut,
    a csorgest a panel mutatja - nem nyitunk kulon ablakot (az elvenne a fokuszt a
    tavoli asztaltol). Ha a mod ki van kapcsolva vagy a panel nincs nyitva, a kezelo
    `false`-t ad, es minden a megszokott modon megy.
  */
  if (handleServiceCall({ channelId, incoming: options.incoming })) return true;

  if (isDesktop()) {
    // Tauriban a `window.open` nem nyit natív ablakot - sajat parancs kell.
    try {
      await invoke('open_huddle_window', { path });
      return true;
    } catch {
      return false;
    }
  }

  if (huddleInline()) {
    // Mar a hivas kepernyojen vagyunk: nincs mit ujranyitni.
    if (window.location.pathname === `/huddle/${channelId}`) return true;
    await navigateInApp(path);
    return true;
  }

  const name = huddleWindowName(channelId);

  /*
    MAR BENT VAGYUNK: az ablakot csak ELOHOZZUK, nem navigaljuk ujra.

    A `window.open(cim, nev)` egy MAR LETEZO ablakot a megadott cimre navigal -
    vagyis ujratolti. Az ujratoltes uj munkamenet-azonositot jelent, a szerver
    pedig jogosan lepteti ki a regit: a felhasznalo kiesik a hivasbol, majd
    ujra belep. Kivulrol ez ugy latszott, mintha az "Ablak" gomb kidobna az
    olelesbol, es a fo ablak meg hibat is irt ki rola.

    Cim NELKUL viszont a bongeszo csak visszaadja a meglevo ablakot.
  */
  if (options.focusExisting) {
    const existing = window.open('', name);
    if (!existing) return false;

    // Ha kozben bezartak, egy ures ablakot kaptunk vissza - azt eligazitjuk.
    if (!existing.location.href || existing.location.href === 'about:blank') {
      existing.location.href = path;
    }

    existing.focus();
    return true;
  }

  const opened = window.open(path, name, huddleWindowFeatures());

  /**
   * A felugro ablakot a bongeszo blokkolhatja.
   *
   * Ez nem elmeleti eset: ha a megnyitas nem kozvetlenul egy kattintasbol
   * indul, a legtobb bongeszo eldobja. A hivo ebbol tudja, hogy szolnia kell
   * a felhasznalonak - nem hagyjuk nema kudarcban.
   */
  if (!opened) return false;

  opened.focus();
  return true;
}

/**
 * Az oleles ablakanak bezarasa - mindket kornyezetben.
 *
 * A `window.close()` Tauriban NEM mukodik: a JS csak olyan ablakot zarhat be,
 * amit o maga nyitott. Az oleles vegen ezert egy ures ablak maradt a
 * kepernyon. A natív ablakot a Rust oldal zarja be.
 */
export async function closeHuddleWindow(): Promise<void> {
  // Vevoszolgalati teljes felulet: csak visszacsukjuk (a window.close() a fo ablakot zarna).
  if (closeServiceView()) return;

  if (isDesktop()) {
    await invoke('close_huddle_window').catch(() => undefined);
    return;
  }
  if (huddleInline()) {
    // Telefonon a hivas az app egy oldala: vissza oda, ahonnan jottunk.
    if (!window.location.pathname.startsWith('/huddle/')) return;
    const { router } = await import('../router');
    if (router.history.canGoBack()) router.history.back();
    else await router.navigate({ to: '/', replace: true });
    return;
  }
  window.close();
}
